/**
 * Libreria utilizzata per impostare i file js di configurazione caricati da
 * una app parent e utilizzati ad esempio per avere dalle varie app l'elenco delle
 * attività da dover gestire per ogni row di dati mantenuti sul parent e relativi
 * alla stessa app
 */

import { getWindowConfig, setWindowConfig } from './windowData';

/**
 * Valore temporaneo utilizzato dal parent per capire
 * se una configurazione è stata caricata
 */
const LoadingConfig = 'loadingConfig...';
const LoadedCallbacks = '_callbacks';

const ConfigActionType = {
  MODAL: 0,
  COMMAND: 1,
};

/**
 * Metodo utilizzato dalle app per predisporre il file
 * di configurazione da inviare al parent che lo richiede
 * @param {string} name - nome dell'applicazione di riferimento
 * @param {object} config - configurazione
 */
const createConfig = (name, config) => {
  setWindowConfig(name, config);
};

/**
 * Funzione utilizzata per impostare una azione che farà
 * aprire un modale al parent con un determinato url
 * @param {string} label - etichetta che dasboard dovrà mostrare in corrispondenza di questa azione
 * @param {string} url
 */
const setActionModal = (label, url) => ({
  type: ConfigActionType.MODAL,
  label,
  url,
});

/**
 * Funzione utilizzata per impostare una azione che dovrà
 * far inoltrare al parent una azione identificata con un determinato url
 * @param {string} label - etichetta che dasboard dovrà mostrare in corrispondenza di questa azione
 * @param {string} url
 */
const setActionCommand = (label, url) => ({
  type: ConfigActionType.COMMAND,
  label,
  url,
});

/**
 * Funzione utilizzata per estrarre l'etichetta dal comando
 * impostato
 * @param {object} action - azione
 */
const getActionLabel = (action) => action.label;

/**
 * Funzione utilizzata per estrarre il tipo dal comando
 * impostato
 * @param {object} action - azione
 */
const getActionType = (action) => action.type;

/**
 * Funzione utilizzata per estrarre l'url dal comando
 * impostato
 * @param {object} action - azione
 */
const getActionUrl = (action) => action.url;

/**
 * Metodo utilizzato dal parent per caricare il file di
 * configurazione di una determinata applicazione
 * @param {string} name - nome dell'applicazione di riferimento
 * @param {string} urlApp - url dell'applicazione da utilizzare per scaricare il file
 * @param {func} afterLoaded - callback chiamata dopo il caricamento del file
 */
const loadConfigScript = (name, urlApp, afterLoaded = null) => {
  const config = getWindowConfig(name);
  if (config) {
    if (afterLoaded) {
      if (config === LoadingConfig) {
        getWindowConfig(name + LoadedCallbacks).push(afterLoaded);
      } else afterLoaded();
    }
    return;
  }
  setWindowConfig(name, LoadingConfig);
  // creo la lista delle funzioni di callback
  setWindowConfig(name + LoadedCallbacks, afterLoaded ? [afterLoaded] : []);
  const e = document.createElement('script');
  e.type = 'text/javascript';
  e.src = `${urlApp}/App/dist/browser-config.js`;
  e.async = false;
  e.defer = false;
  e.onload = () => {
    // chiamo tutti i callback dei caricamenti richiesti
    getWindowConfig(name + LoadedCallbacks).forEach((callback) => callback());
    setWindowConfig(name + LoadedCallbacks, null);
  };
  const h = document.getElementsByTagName('head')[0];
  h.insertBefore(e, h.lastChild);
};

/**
 * Funzione che verifica se è stato caricato la sezione
 * di configurazione di una specifica applicazione
 * @param {string} name - nome dell'applicazione di riferimento
 */
const isLoadedConfig = (name) => {
  const conf = getWindowConfig(name);
  return (conf && conf !== LoadingConfig) || false;
};

/**
 * Funzione utilizzata per generare un uuid univoco
 */
const getUUID = () => Math.random().toString(16).slice(2);

export {
  LoadedCallbacks, // only for test
  createConfig,
  setActionModal,
  setActionCommand,
  loadConfigScript,
  isLoadedConfig,
  getActionLabel,
  getActionType,
  getActionUrl,
  ConfigActionType,
  getUUID,
};
